import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Briefcase, UserPlus, CheckCircle2, FileText, Clock, Mail, Trash2, X, ShieldCheck } from "lucide-react";

// ─── Steuerberater Access Section ─── 
function AdvisorAccessSection() {
  const { user } = useAuth();
  const [advisors, setAdvisors] = useState([]);
  const [invitations, setInvitations] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  // Feb 2026 — Pro-Gating: Free/Basic-User dürfen SB nicht einladen/annehmen.
  // Backend lehnt das eh ab (HTTP 403); hier zeigen wir einen klaren Upgrade-Hinweis,
  // damit der User nicht in einen API-Fehler läuft.
  const PRO_PLANS = new Set(['pro', 'pro_monthly', 'pro_yearly', 'lifetime']);
  const isPro = !!user && (
    !!user.has_lifetime_access || PRO_PLANS.has((user.plan || 'free').toLowerCase())
  );

  const fetchData = async () => {
    try {
      const [advRes, invRes] = await Promise.all([
        api.get('/advisor/my-advisors'),
        api.get('/advisor/invitations/freelancer'),
      ]);
      setAdvisors(advRes.data.advisors || []);
      setInvitations(invRes.data || { sent: [], received: [] });
    } catch (err) {
      // Non-fatal: User hat noch keinen SB verbunden ODER Endpoint kurz nicht
      // erreichbar. Wir loggen für Debugging, zeigen aber keine Toast — der
      // leere Zustand wird ohnehin korrekt gerendert.
      console.warn('[AdvisorAccessSection] fetchData failed:', err?.response?.status, err?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleInviteAdvisor = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await api.post('/advisor/invitations/freelancer-create', {
        to_email: inviteEmail.trim(),
      });
      toast.success('Einladung gesendet!');
      setInviteEmail('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    } finally { setInviting(false); }
  };

  const handleToggleVollmacht = async (relId, value) => {
    try {
      await api.put(`/advisor/relationship/${relId}/permissions`, { edit: value });
      fetchData();
      toast.success(value ? 'Vollmacht erteilt' : 'Vollmacht entzogen');
    } catch { toast.error('Fehler'); }
  };

  const handleRevokeAccess = async (relId) => {
    if (!window.confirm('Zugang wirklich widerrufen?')) return;
    try {
      await api.post(`/advisor/relationship/${relId}/revoke`);
      fetchData();
      toast.success('Zugang widerrufen');
    } catch { toast.error('Fehler'); }
  };

  const handleAcceptInvitation = async (token) => {
    try {
      await api.post(`/advisor/invitations/${token}/accept-freelancer`);
      fetchData();
      toast.success('Einladung angenommen!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleRejectInvitation = async (token) => {
    if (!window.confirm('Einladung wirklich ablehnen?')) return;
    try {
      await api.post(`/advisor/invitations/${token}/reject-freelancer`);
      fetchData();
      toast.success('Einladung abgelehnt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleCancelSentInvitation = async (token) => {
    if (!window.confirm('Gesendete Einladung wirklich stornieren?')) return;
    try {
      await api.post(`/advisor/invitations/${token}/cancel-freelancer`);
      fetchData();
      toast.success('Einladung storniert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center">Laden...</div>;

  // Feb 2026 — Pro-Gate: Free/Basic-User sehen Upgrade-Hinweis statt Invite-UI.
  if (!isPro) {
    return (
      <Card className="border-emerald-200" data-testid="advisor-access-pro-gate">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Steuerberater-Anbindung — Pro-Feature
          </CardTitle>
          <CardDescription>
            Verbinde deinen Steuerberater direkt in ivoliq, gemeinsam Belege prüfen, Quartale freigeben — alles DSGVO-konform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              Eigener Posteingang mit Threading & Anhängen
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              Dokumente: GoBD-konformer Dokumenten-Speicher
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              Quartalsweise Abschlüsse + Pre-Check + PDF-Bericht
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              Direkter ZIP-/DATEV-Export für deinen SB
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              Vollmacht digital — keine Papierkram-Schleifen
            </li>
          </ul>
          <Button
            onClick={() => { window.location.href = '/pricing'; }}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="advisor-access-upgrade-cta"
          >
            Auf Pro upgraden
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite Advisor */}
      <Card data-testid="advisor-invite-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Steuerberater einladen
          </CardTitle>
          <CardDescription>
            Laden Sie Ihren Steuerberater ein, um ihm Zugang zu Ihren Finanzdaten zu geben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="steuerberater@kanzlei.de"
              className="flex-1"
              data-testid="settings-advisor-invite-email"
            />
            <Button
              onClick={handleInviteAdvisor}
              disabled={inviting || !inviteEmail.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="settings-advisor-invite-btn"
            >
              {inviting ? 'Senden...' : 'Einladen'}
            </Button>
          </div>
          {/* C5-Cleanup (Feb 2026): "Magic Link erstellen"-Block entfernt — Magic-Link
              ist nicht mehr Teil der UI; SB-Einladung läuft ausschließlich per E-Mail. */}
        </CardContent>
      </Card>

      {/* Pending received invitations */}
      {invitations.received?.filter(i => i.status === 'pending' && i.type === 'advisor_to_freelancer').length > 0 && (
        <Card data-testid="advisor-pending-invitations">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Eingehende Einladungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invitations.received.filter(i => i.status === 'pending' && i.type === 'advisor_to_freelancer').map((inv) => (
              <div key={inv.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div>
                  <div className="text-sm font-medium text-slate-900">{inv.from_user_name || 'Steuerberater'}</div>
                  <div className="text-xs text-slate-500">Einladung erhalten · {new Date(inv.created_at).toLocaleDateString('de-DE')}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectInvitation(inv.token)}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    data-testid={`reject-inv-${inv.id}`}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Ablehnen
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptInvitation(inv.token)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`accept-inv-${inv.id}`}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Annehmen
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pending sent invitations (Feb 2026 — FL kann eigene Einladungen sehen + stornieren) */}
      {invitations.sent?.filter(i => i.status === 'pending' && i.type === 'freelancer_to_advisor').length > 0 && (
        <Card data-testid="advisor-sent-invitations">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Gesendete Einladungen
            </CardTitle>
            <CardDescription>
              Einladungen, die noch nicht angenommen wurden. Du kannst sie jederzeit stornieren.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitations.sent.filter(i => i.status === 'pending' && i.type === 'freelancer_to_advisor').map((inv) => (
              <div key={inv.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{inv.to_email}</div>
                  <div className="text-xs text-slate-500">
                    Gesendet am {new Date(inv.created_at).toLocaleDateString('de-DE')}
                    {inv.expires_at && (
                      <> · läuft ab am {new Date(inv.expires_at).toLocaleDateString('de-DE')}</>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCancelSentInvitation(inv.token)}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 shrink-0"
                  data-testid={`cancel-sent-inv-${inv.id}`}
                >
                  <X className="h-3 w-3 mr-1" />
                  Stornieren
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Connected Advisors with Permissions */}
      <Card data-testid="advisor-connected-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Verbundene Steuerberater
          </CardTitle>
          <CardDescription>
            Verwalten Sie die Zugriffsrechte Ihrer verbundenen Steuerberater.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {advisors.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Noch kein Steuerberater verbunden. Senden Sie eine Einladung oben.
            </p>
          ) : (
            <div className="space-y-4">
              {advisors.map((adv) => (
                <div key={adv.relationship_id} className="border rounded-xl p-4" data-testid={`advisor-${adv.advisor_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{adv.name}</div>
                      <div className="text-xs text-slate-500">{adv.email}</div>
                      {adv.company_name && (
                        <div className="text-xs text-slate-400">{adv.company_name}</div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevokeAccess(adv.relationship_id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`revoke-${adv.relationship_id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Zugang widerrufen
                    </Button>
                  </div>
                  {/* P1b-Simplification (Feb 2026):
                   * Granulare Permissions-Matrix → 1 Vollmacht-Toggle.
                   * Read-Access ist immer aktiv für aktive Verbindungen;
                   * nur das „Bearbeiten" (Vollmacht) ist konfigurierbar. */}
                  {(() => {
                    const hasVollmacht = adv.permissions?.edit === true;
                    return (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`vollmacht-toggle-${adv.relationship_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <div className={`rounded-md p-1.5 shrink-0 ${hasVollmacht ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                              <ShieldCheck className={`h-4 w-4 ${hasVollmacht ? 'text-emerald-700' : 'text-slate-500'}`} strokeWidth={1.7} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900">
                                Vollmacht für Steuerberater erteilen
                              </div>
                              <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                                {hasVollmacht
                                  ? 'Aktiviert: Dein Steuerberater darf Belege & Buchungen direkt korrigieren. Jede Änderung wird DSGVO-konform geloggt und ist im Änderungslog einsehbar.'
                                  : 'Deaktiviert: Dein Steuerberater sieht alle Daten, kann sie aber nicht ändern. Nur-Lese-Zugriff.'}
                              </div>
                            </div>
                          </div>
                          <Switch
                            checked={hasVollmacht}
                            onCheckedChange={(v) => handleToggleVollmacht(adv.relationship_id, v)}
                            data-testid={`vollmacht-switch-${adv.relationship_id}`}
                            className="shrink-0 mt-1"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Änderungslog ist jetzt im FL-Steuerberater-Tab integriert (siehe Posteingang → Änderungslog) */}
    </div>
  );
}


// ─── Änderungslog: Shows all changes made by advisors ───
function AdvisorChangeLog() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logRes, commentRes] = await Promise.all([
          api.get(`/advisor/audit-log/${user?.id}`),
          api.get(`/advisor/clients/${user?.id}/comments`),
        ]);
        setEntries(logRes.data.entries || []);
        setComments((commentRes.data.comments || []).filter(c => c.entity_type === 'expense' && c.entity_id));
      } catch (err) {
        // Non-fatal: kein SB verbunden ODER noch keine Log-Einträge — beides ist
        // ein erwarteter Zustand. Wir loggen für Debugging und rendern still „nichts".
        console.warn('[AdvisorChangeLog] fetchData failed:', err?.response?.status, err?.message);
      }
      finally { setLoading(false); }
    };
    if (user?.id) fetchData();
  }, [user?.id]);

  const handleRespondComment = async (commentId, action) => {
    try {
      await api.post(`/advisor/comments/${commentId}/respond`, { action });
      toast.success(action === 'confirm' ? 'Bestätigt' : 'Abgelehnt');
      // Refresh
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, response_status: action } : c));
    } catch {
      toast.error('Fehler');
    }
  };

  const FIELD_LABELS = {
    status: 'Status', category: 'Kategorie', description: 'Beschreibung',
    amount: 'Betrag', date: 'Datum', vat_rate: 'MwSt %', vendor: 'Händler',
    invoice_number: 'Rechnungsnr.', notes: 'Notizen', net_amount: 'Nettobetrag',
    is_deductible: 'Absetzbar', deductible_percentage: 'Absetzbar %',
    tax_deductible_type: 'Absetzbarkeit',
  };
  const ENTITY_LABELS = { expense: 'Ausgabe', invoice: 'Rechnung' };
  const CAT_LABELS = {
    office: 'Büromaterial', software: 'Software & Lizenzen', travel: 'Reisekosten',
    hospitality: 'Bewirtung', vehicle: 'Fahrzeug & Fahrkosten', telecom: 'Telefon & Internet',
    insurance: 'Versicherungen', education: 'Fortbildung', marketing: 'Werbung & Marketing',
    rent: 'Miete & Nebenkosten', consulting: 'Beratung (StB/Anwalt)', equipment: 'Betriebsausstattung',
    other: 'Sonstiges',
  };
  const fmtValue = (field, val) => {
    if (field === 'category') return CAT_LABELS[val] || val;
    if (field === 'amount' || field === 'net_amount') {
      const n = parseFloat(val);
      return isNaN(n) ? val : `${n.toFixed(2)} €`;
    }
    return val;
  };

  if (loading) return null;
  if (entries.length === 0 && comments.length === 0) return null;

  const pendingComments = comments.filter(c => !c.response_status);

  return (
    <>
      {/* Pending Expense Comments from Advisor (needs action) */}
      {pendingComments.length > 0 && (
        <Card className="border-blue-200" data-testid="advisor-pending-comments">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" />
              Offene SB-Anmerkungen ({pendingComments.length})
            </CardTitle>
            <CardDescription>Ihr Steuerberater hat Anmerkungen zu Ausgaben hinterlassen. Bitte bestätigen oder ablehnen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingComments.map(c => (
              <div key={c.id} className="flex items-start justify-between gap-3 p-3 bg-blue-50 border border-blue-100 rounded" data-testid={`pending-comment-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 mb-1">
                    <span className="font-medium text-slate-700">{c.advisor_name || 'Steuerberater'}</span>
                    {' · '}{new Date(c.created_at).toLocaleDateString('de-DE')}
                    {' · Ausgabe'}
                  </div>
                  <p className="text-sm text-slate-800">{c.text}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => handleRespondComment(c.id, 'confirm')}
                    className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                    data-testid={`confirm-comment-${c.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" />OK
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRespondComment(c.id, 'reject')}
                    className="h-7 text-xs bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                    data-testid={`reject-comment-${c.id}`}>
                    <X className="h-3 w-3 mr-1" />Nein
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Full Change Log */}
      {entries.length > 0 && (
        <Card data-testid="advisor-changelog">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Änderungslog (Steuerberater)
            </CardTitle>
            <CardDescription>Alle Änderungen, die Ihr Steuerberater an Ihren Daten vorgenommen hat.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {entries.slice(0, 50).map(e => (
                <div key={e.id} className="flex items-start gap-3 px-3 py-2.5 bg-zinc-50 border border-zinc-100 rounded text-sm" data-testid={`log-entry-${e.id}`}>
                  <div className="shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 mb-0.5">
                      <span className="font-medium text-slate-700">{e.advisor_name || 'Steuerberater'}</span>
                      {' · '}{new Date(e.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-slate-800">
                      <span className="font-medium">{ENTITY_LABELS[e.entity_type] || e.entity_type}</span>
                      {e.extra?.invoice_number && <span className="text-slate-500"> ({e.extra.invoice_number})</span>}
                      {': '}
                      <span className="text-slate-600">{FIELD_LABELS[e.field] || e.field}</span>
                      {' '}
                      <span className="text-red-500 line-through">{fmtValue(e.field, e.old_value) || '—'}</span>
                      {' → '}
                      <span className="text-emerald-700 font-medium">{fmtValue(e.field, e.new_value)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export { AdvisorAccessSection, AdvisorChangeLog };
